<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import LoadingState from '../../../components/widgets/LoadingState.vue';
import RealEstateIcon from './RealEstateIcon.vue';
import OverviewSection from './sections/OverviewSection.vue';
import LocationSection from './sections/LocationSection.vue';
import LegalSection from './sections/LegalSection.vue';
import FeaturesSection from './sections/FeaturesSection.vue';
import NearbySection from './sections/NearbySection.vue';
import FloorPlansSection from './sections/FloorPlansSection.vue';
import GallerySection from './sections/GallerySection.vue';
import BrochuresSection from './sections/BrochuresSection.vue';
import BookingSection from './sections/BookingSection.vue';
import AssignedAgentsSection from './sections/AssignedAgentsSection.vue';
import SchedulesSection from './sections/SchedulesSection.vue';
import AssignmentsSection from './sections/AssignmentsSection.vue';
import SalesGallerySection from './sections/SalesGallerySection.vue';
import OverviewModal from './modals/OverviewModal.vue';
import LocationModal from './modals/LocationModal.vue';
import LegalModal from './modals/LegalModal.vue';
import SalesModal from './modals/SalesModal.vue';
import GalleryModal from './modals/GalleryModal.vue';
import BrochuresModal from './modals/BrochuresModal.vue';
import UnitModal from './modals/UnitModal.vue';
import BookingModal from './modals/BookingModal.vue';
import OpeningHoursModal from './modals/OpeningHoursModal.vue';
import AssignedAgentsModal from './modals/AssignedAgentsModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import ActiveSwitch from './components/ActiveSwitch.vue';
import { apiErrorCode, apiErrorMessage } from '../../../api/realEstate/errors';
import { reindexEmbedding } from '../../../api/realEstate/properties';
import { STATUSES, FIELD_CLASS, SELECT_CLASS, HUES } from './constants';
import { hashIndex, sizeRange, sizeUnitLabel } from './helpers';

const ANCHORS = [
  ['overview', 'REAL_ESTATE.OVERVIEW.TITLE'],
  ['location', 'REAL_ESTATE.LOCATION.TITLE'],
  ['legal', 'REAL_ESTATE.LEGAL.TITLE'],
  ['features', 'REAL_ESTATE.FEATURES.TITLE'],
  ['nearby', 'REAL_ESTATE.NEARBY.TITLE'],
  ['plans', 'REAL_ESTATE.PLANS.TITLE'],
  ['gallery', 'REAL_ESTATE.GALLERY.TITLE'],
  ['brochures', 'REAL_ESTATE.BROCHURES.TITLE'],
  ['booking', 'REAL_ESTATE.BOOKING.TITLE'],
  ['agents', 'REAL_ESTATE.ASSIGNED_AGENTS.TITLE'],
  ['schedules', 'REAL_ESTATE.SCHEDULES.TITLE'],
  ['assignments', 'REAL_ESTATE.ASSIGNMENTS.TITLE'],
  ['sales', 'REAL_ESTATE.SALES.TITLE'],
];

// A section counts as the one being read once its top passes this far
// below the sticky section bar.
const ACTIVE_LINE_OFFSET = 24;
// A click's smooth scroll counts as finished after this long without a
// scroll event; until then the clicked link stays active.
const SCROLL_IDLE_MS = 150;
// How long the section a jump lands on keeps its ring before fading.
const HIGHLIGHT_MS = 900;
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-woot-500'];

// The project's own detail is fetched on mount (and whenever the route's
// projectId changes) rather than read synchronously off a getter — it now
// lives behind a real GET request (mock or BE), and cover/gallery image
// URLs are presigned and expire after an hour, so a stale cached copy from
// an earlier visit shouldn't be trusted either (docs/real-estate/be/
// 10-api-reference.md §0.3).
export default {
  name: 'ProjectDetail',
  components: {
    LoadingState,
    RealEstateIcon,
    OverviewSection,
    LocationSection,
    LegalSection,
    FeaturesSection,
    NearbySection,
    FloorPlansSection,
    GallerySection,
    BrochuresSection,
    BookingSection,
    AssignedAgentsSection,
    SchedulesSection,
    AssignmentsSection,
    SalesGallerySection,
    OverviewModal,
    LocationModal,
    LegalModal,
    SalesModal,
    GalleryModal,
    BrochuresModal,
    UnitModal,
    BookingModal,
    OpeningHoursModal,
    AssignedAgentsModal,
    DeleteModal,
    ActiveSwitch,
  },
  data() {
    return {
      isLoading: false,
      loadError: false,
      anchors: ANCHORS,
      statuses: STATUSES,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      activeModal: null,
      editingUnit: null,
      isTogglingActive: false,
      isDeleteModalOpen: false,
      isDeletingProject: false,
      deleteBlocked: false,
      isReindexing: false,
      // Section bar: the link marked as the section being read, and
      // whether the bar is stuck to the top (it gets a shadow then).
      activeSection: ANCHORS[0][0],
      isNavStuck: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', { getProject: 'getProject' }),
    project() {
      return this.getProject(this.$route.params.projectId);
    },
    // pending -> amber, ready -> green, anything else -> red (showing the
    // raw status) — docs/real-estate/be/10-api-reference.md §A2.
    embeddingStatusLabel() {
      if (this.project.embedding_status === 'pending') {
        return this.$t('REAL_ESTATE.DETAIL.EMBEDDING_PENDING');
      }
      if (this.project.embedding_status === 'ready') {
        return this.$t('REAL_ESTATE.DETAIL.EMBEDDING_READY');
      }
      return this.project.embedding_status;
    },
    embeddingStatusStyle() {
      if (this.project.embedding_status === 'pending') {
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300';
      }
      if (this.project.embedding_status === 'ready') {
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      }
      return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
    },
    embeddingDotStyle() {
      if (this.project.embedding_status === 'pending') return 'bg-amber-500';
      if (this.project.embedding_status === 'ready') return 'bg-green-500';
      return 'bg-red-500';
    },
    // Same decorative gradient as ProjectCard when there's no cover image.
    heroStyle() {
      // Only the hue is set here; .re-cover-gradient (below) draws the
      // gradient.
      if (this.project.cover_image_url) return {};
      return {
        '--re-hue': HUES[hashIndex(this.project.property_id, HUES.length)],
      };
    },
    // Inline rather than a Tailwind gradient: the theme's replaced palette
    // doesn't reliably support /opacity on every colour.
    heroOverlayStyle() {
      return {
        background:
          'linear-gradient(to top, rgba(15, 23, 42, 0.65), rgba(15, 23, 42, 0))',
      };
    },
    heroBadges() {
      return [this.project.buyer_restriction, this.project.tenure].filter(
        Boolean
      );
    },
    // Same size range as the project card (the rollup's size_min/max, in
    // size_unit — sqft when the floor plans mix units). null = no sizes.
    sizeRangeLabel() {
      const { rollup } = this.project;
      const range = rollup ? sizeRange(rollup) : null;
      if (range == null) return null;
      return this.$t('REAL_ESTATE.CARD.SIZE_RANGE', {
        range,
        unit: sizeUnitLabel(this, rollup.size_unit),
      });
    },
    heroLocation() {
      return [this.project.area, this.project.region]
        .filter(Boolean)
        .join(' · ');
    },
  },
  watch: {
    '$route.params.projectId': {
      immediate: true,
      handler() {
        this.activeSection = ANCHORS[0][0];
        this.loadProject();
      },
    },
    activeSection() {
      this.$nextTick(this.revealActiveLink);
    },
  },
  created() {
    // Not reactive: timers and the section a click is scrolling to.
    this.scrollFrame = null;
    this.scrollIdleTimer = null;
    this.clickedSection = null;
    this.highlightTimer = null;
    this.highlightedEl = null;
  },
  beforeDestroy() {
    cancelAnimationFrame(this.scrollFrame);
    clearTimeout(this.scrollIdleTimer);
    this.clearHighlight();
  },
  methods: {
    ...mapActions('realEstate', [
      'fetchProject',
      'setStatus',
      'setActive',
      'deleteProject',
    ]),
    async loadProject() {
      this.isLoading = true;
      this.loadError = false;
      try {
        await this.fetchProject(this.$route.params.projectId);
      } catch (err) {
        this.loadError = true;
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    // The sections live in an inner scroll container, so a bare `#sec-…`
    // href only jumps. Scroll smoothly instead, unless the user has asked
    // the OS for reduced motion. The clicked link is marked active straight
    // away and held there until the scroll settles, so the bar doesn't step
    // through every section on the way.
    scrollToSection(id) {
      const el = document.getElementById(`sec-${id}`);
      if (!el) return;
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      this.activeSection = id;
      this.clickedSection = id;
      this.armScrollIdle();
      this.highlightSection(el);
      el.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    },
    // Scroll events arrive every frame while scrolling; handle one per frame.
    handleScroll() {
      if (this.clickedSection) this.armScrollIdle();
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateSectionBar();
      });
    },
    armScrollIdle() {
      clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = setTimeout(() => {
        this.clickedSection = null;
      }, SCROLL_IDLE_MS);
    },
    updateSectionBar() {
      const { scroller, sectionNav } = this.$refs;
      if (!scroller || !sectionNav) return;
      const navRect = sectionNav.getBoundingClientRect();
      this.isNavStuck =
        scroller.scrollTop > 0 &&
        navRect.top <= scroller.getBoundingClientRect().top + 1;
      if (this.clickedSection) return;

      const sections = ANCHORS.map(([id]) => ({
        id,
        el: document.getElementById(`sec-${id}`),
      })).filter(section => section.el);
      if (!sections.length) return;

      // The last sections can't scroll up to the bar, so at the very bottom
      // keep a section that's in view (e.g. the one just jumped to), else
      // mark the last one.
      const distanceToBottom =
        scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
      const atBottom = distanceToBottom < 2;
      if (atBottom) {
        const current = sections.find(s => s.id === this.activeSection);
        const currentInView =
          current && current.el.getBoundingClientRect().top >= navRect.bottom;
        if (!currentInView) {
          this.activeSection = sections[sections.length - 1].id;
        }
        return;
      }

      const line = navRect.bottom + ACTIVE_LINE_OFFSET;
      let active = sections[0].id;
      sections.forEach(section => {
        if (section.el.getBoundingClientRect().top <= line) {
          active = section.id;
        }
      });
      this.activeSection = active;
    },
    // On narrow screens the bar scrolls sideways — keep the active link in
    // view without moving the page vertically.
    revealActiveLink() {
      const { sectionNav } = this.$refs;
      if (!sectionNav) return;
      const link = sectionNav.querySelector(
        `[data-section="${this.activeSection}"]`
      );
      if (!link) return;
      const left =
        link.getBoundingClientRect().left -
        sectionNav.getBoundingClientRect().left +
        sectionNav.scrollLeft;
      const right = left + link.offsetWidth;
      // Keep the bar's side padding in view too, so a link at either end
      // doesn't end up flush against the edge.
      const margin = parseFloat(getComputedStyle(sectionNav).paddingLeft);
      if (left - margin < sectionNav.scrollLeft) {
        sectionNav.scrollLeft = Math.max(0, left - margin);
      } else if (
        right + margin >
        sectionNav.scrollLeft + sectionNav.clientWidth
      ) {
        sectionNav.scrollLeft = right + margin - sectionNav.clientWidth;
      }
    },
    // A short ring on the section a jump lands on — the last sections stop
    // mid-screen, so this shows which one it was. SectionCard fades it out.
    highlightSection(el) {
      this.clearHighlight();
      el.classList.add(...HIGHLIGHT_CLASSES);
      this.highlightedEl = el;
      this.highlightTimer = setTimeout(this.clearHighlight, HIGHLIGHT_MS);
    },
    clearHighlight() {
      clearTimeout(this.highlightTimer);
      if (this.highlightedEl) {
        this.highlightedEl.classList.remove(...HIGHLIGHT_CLASSES);
        this.highlightedEl = null;
      }
    },
    sectionLinkClass(id) {
      return id === this.activeSection
        ? 'text-woot-500 border-woot-500'
        : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-800 hover:border-slate-300 dark:hover:text-slate-200 dark:hover:border-slate-600';
    },
    openModal(name) {
      this.activeModal = name;
    },
    closeModal() {
      this.activeModal = null;
      this.editingUnit = null;
    },
    openAddUnit() {
      this.editingUnit = null;
      this.activeModal = 'unit';
    },
    openEditUnit(unit) {
      this.editingUnit = unit;
      this.activeModal = 'unit';
    },
    async handleStatusChange(event) {
      const projectStatus = event.target.value;
      try {
        await this.setStatus({
          propertyId: this.project.property_id,
          projectStatus,
        });
        useAlert(
          this.$t('REAL_ESTATE.DETAIL.SUCCESS_STATUS', {
            status: this.statuses[projectStatus],
          })
        );
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleToggleActive() {
      this.isTogglingActive = true;
      const isActive = !this.project.is_active;
      try {
        await this.setActive({
          propertyId: this.project.property_id,
          isActive,
        });
        useAlert(
          this.$t(
            isActive
              ? 'REAL_ESTATE.CARD.SUCCESS_ACTIVE'
              : 'REAL_ESTATE.CARD.SUCCESS_INACTIVE',
            { name: this.project.name }
          )
        );
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isTogglingActive = false;
      }
    },
    async confirmDeleteProject() {
      this.isDeletingProject = true;
      try {
        await this.deleteProject(this.project.property_id);
        useAlert(this.$t('REAL_ESTATE.DETAIL.SUCCESS_DELETED'));
        this.$router.push({
          name: 'real_estate',
          params: { accountId: this.$route.params.accountId },
        });
      } catch (err) {
        this.isDeleteModalOpen = false;
        if (apiErrorCode(err) === 'has_appointments') {
          this.deleteBlocked = true;
        } else {
          useAlert(apiErrorMessage(this, err));
        }
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeletingProject = false;
      }
    },
    async handleReindex() {
      this.isReindexing = true;
      try {
        await reindexEmbedding(this.project.property_id, true);
        useAlert(this.$t('REAL_ESTATE.DETAIL.SUCCESS_REINDEX'));
        await this.fetchProject(this.project.property_id);
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.DETAIL.ERROR_REINDEX')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isReindexing = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full overflow-hidden flex flex-col flex-1 min-w-0">
    <LoadingState
      v-if="isLoading"
      :message="$t('REAL_ESTATE.DETAIL.LOADING')"
    />

    <template v-else-if="project">
      <OverviewModal
        v-if="activeModal === 'overview'"
        :project="project"
        @close="closeModal"
      />
      <LocationModal
        v-if="activeModal === 'location'"
        :project="project"
        @close="closeModal"
      />
      <LegalModal
        v-if="activeModal === 'legal'"
        :project="project"
        @close="closeModal"
      />
      <SalesModal
        v-if="activeModal === 'sales'"
        :project="project"
        @close="closeModal"
      />
      <GalleryModal
        v-if="activeModal === 'gallery'"
        :project="project"
        @close="closeModal"
      />
      <BrochuresModal
        v-if="activeModal === 'brochures'"
        :project="project"
        @close="closeModal"
      />
      <UnitModal
        v-if="activeModal === 'unit'"
        :project="project"
        :unit="editingUnit"
        @close="closeModal"
      />
      <BookingModal
        v-if="activeModal === 'booking'"
        :project="project"
        @close="closeModal"
      />
      <OpeningHoursModal
        v-if="activeModal === 'openingHours'"
        :project="project"
        @close="closeModal"
      />
      <AssignedAgentsModal
        v-if="activeModal === 'agents'"
        :project="project"
        @close="closeModal"
      />

      <div
        ref="scroller"
        class="flex-1 overflow-y-auto"
        @scroll.passive="handleScroll"
      >
        <div class="p-4 md:p-8 pb-0">
          <div
            class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-3"
          >
            <router-link
              :to="{
                name: 'real_estate',
                params: { accountId: $route.params.accountId },
              }"
              class="hover:underline"
            >
              {{ $t('REAL_ESTATE.DETAIL.BREADCRUMB_ROOT') }}
            </router-link>
            <span>/</span>
            <span>{{ project.name }}</span>
          </div>

          <div
            class="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
            :class="{ 're-cover-gradient': !project.cover_image_url }"
            :style="heroStyle"
          >
            <img
              v-if="project.cover_image_url"
              :src="project.cover_image_url"
              :alt="project.name"
              class="absolute inset-0 w-full h-full object-cover"
              @error="$event.target.remove()"
            />
            <div
              class="relative flex flex-col justify-end gap-4 min-h-[220px] p-6 md:p-10 text-white"
              :style="heroOverlayStyle"
            >
              <div v-if="heroBadges.length" class="flex flex-wrap gap-2">
                <span
                  v-for="badge in heroBadges"
                  :key="badge"
                  class="bg-white/90 text-slate-800 px-2.5 py-1 rounded-md text-xs font-semibold"
                >
                  {{ badge }}
                </span>
              </div>
              <div class="flex flex-wrap items-end justify-between gap-4">
                <div class="min-w-0">
                  <h1
                    class="mb-0 text-3xl md:text-4xl font-extrabold tracking-tight text-white"
                  >
                    {{ project.name }}
                  </h1>
                  <p
                    v-if="heroLocation"
                    class="mb-0 mt-1 flex items-center gap-1 text-sm text-white opacity-90"
                  >
                    <fluent-icon icon="location" size="14" />
                    {{ heroLocation }}
                  </p>
                </div>
                <div class="flex flex-wrap gap-4 text-sm font-medium">
                  <span v-if="sizeRangeLabel" class="flex items-center gap-1.5">
                    <RealEstateIcon icon="ruler" :size="16" />
                    {{ sizeRangeLabel }}
                  </span>
                  <span
                    v-if="project.rollup.max_bedrooms"
                    class="flex items-center gap-1.5"
                  >
                    <RealEstateIcon icon="bed" :size="16" />
                    {{
                      $tc(
                        'REAL_ESTATE.DETAIL.UP_TO_BEDROOMS',
                        project.rollup.max_bedrooms,
                        { count: project.rollup.max_bedrooms }
                      )
                    }}
                  </span>
                  <span
                    v-if="project.rollup.max_bathrooms"
                    class="flex items-center gap-1.5"
                  >
                    <RealEstateIcon icon="bath" :size="16" />
                    {{
                      $tc(
                        'REAL_ESTATE.DETAIL.UP_TO_BATHROOMS',
                        project.rollup.max_bathrooms,
                        { count: project.rollup.max_bathrooms }
                      )
                    }}
                  </span>
                  <span
                    v-if="project.rollup.max_parking_lots"
                    class="flex items-center gap-1.5"
                  >
                    <RealEstateIcon icon="car" :size="16" />
                    {{
                      $tc(
                        'REAL_ESTATE.DETAIL.UP_TO_PARKING',
                        project.rollup.max_parking_lots,
                        { count: project.rollup.max_parking_lots }
                      )
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div class="flex items-center gap-3">
                <ActiveSwitch
                  :value="project.is_active"
                  :disabled="isTogglingActive"
                  show-label
                  @toggle="handleToggleActive"
                />
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    project.is_active
                      ? $t('REAL_ESTATE.DETAIL.ACTIVE_HELP')
                      : $t('REAL_ESTATE.DETAIL.INACTIVE_HELP')
                  }}
                </span>
              </div>
              <div
                class="flex items-center gap-2 sm:pl-6 sm:border-l border-slate-200 dark:border-slate-700"
              >
                <label class="mb-0 text-xs font-medium text-slate-500">
                  {{ $t('REAL_ESTATE.DETAIL.STATUS_LABEL') }}
                </label>
                <select
                  :value="project.project_status"
                  :class="selectClass"
                  class="max-w-[220px]"
                  @change="handleStatusChange"
                >
                  <option
                    v-for="(label, key) in statuses"
                    :key="key"
                    :value="key"
                  >
                    {{ label }}
                  </option>
                </select>
              </div>
              <div
                class="flex items-center gap-2 sm:pl-6 sm:border-l border-slate-200 dark:border-slate-700"
              >
                <label class="mb-0 text-xs font-medium text-slate-500">
                  {{ $t('REAL_ESTATE.DETAIL.EMBEDDING_LABEL') }}
                </label>
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                  :class="embeddingStatusStyle"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="embeddingDotStyle"
                  />
                  {{ embeddingStatusLabel }}
                </span>
                <woot-button
                  variant="clear"
                  size="small"
                  icon="arrow-clockwise"
                  :is-loading="isReindexing"
                  @click="handleReindex"
                >
                  {{ $t('REAL_ESTATE.DETAIL.REINDEX_BUTTON') }}
                </woot-button>
              </div>
            </div>
            <woot-button
              variant="clear"
              color-scheme="alert"
              icon="delete"
              :title="$t('REAL_ESTATE.DETAIL.DELETE_BUTTON')"
              @click="isDeleteModalOpen = true"
            />
          </div>

          <DeleteModal
            v-if="isDeleteModalOpen"
            :delete-name="project.name"
            :is-loading="isDeletingProject"
            @confirm="confirmDeleteProject"
            @close="isDeleteModalOpen = false"
          />

          <p
            v-if="!project.is_active"
            class="mt-3 flex items-center gap-2 rounded-md bg-amber-50 text-amber-700 text-sm px-3 py-2"
          >
            <fluent-icon icon="warning" size="14" />
            {{ $t('REAL_ESTATE.DETAIL.INACTIVE_BANNER') }}
          </p>

          <p
            v-if="deleteBlocked"
            class="mt-3 flex items-center gap-2 rounded-md bg-amber-50 text-amber-700 text-sm px-3 py-2"
          >
            <fluent-icon icon="warning" size="14" />
            <span>{{ $t('REAL_ESTATE.DETAIL.DELETE_HAS_APPOINTMENTS') }}</span>
            <router-link
              :to="{
                name: 'real_estate_appointments',
                query: { property: project.property_id },
              }"
              class="text-woot-500 hover:underline font-medium shrink-0"
            >
              {{ $t('REAL_ESTATE.DETAIL.DELETE_HAS_APPOINTMENTS_LINK') }}
            </router-link>
          </p>
        </div>

        <nav
          ref="sectionNav"
          class="sticky top-0 z-10 mt-4 px-4 md:px-8 flex gap-5 overflow-x-auto border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 transition-shadow"
          :class="{ 'shadow-md': isNavStuck }"
        >
          <a
            v-for="[id, labelKey] in anchors"
            :key="id"
            :href="`#sec-${id}`"
            :data-section="id"
            :aria-current="id === activeSection ? 'location' : null"
            class="py-[18px] whitespace-nowrap text-sm font-medium border-b-2 transition-colors"
            :class="sectionLinkClass(id)"
            @click.prevent="scrollToSection(id)"
          >
            {{ $t(labelKey) }}
          </a>
        </nav>

        <div class="p-4 md:p-8 pt-6 space-y-4">
          <OverviewSection :project="project" @edit="openModal('overview')" />
          <LocationSection :project="project" @edit="openModal('location')" />
          <LegalSection :project="project" @edit="openModal('legal')" />
          <FeaturesSection :project="project" />
          <NearbySection :project="project" />
          <FloorPlansSection
            :project="project"
            @add="openAddUnit"
            @edit="openEditUnit"
          />
          <GallerySection :project="project" @edit="openModal('gallery')" />
          <BrochuresSection :project="project" @edit="openModal('brochures')" />
          <BookingSection
            :project="project"
            @edit="openModal('booking')"
            @editHours="openModal('openingHours')"
          />
          <AssignedAgentsSection
            :project="project"
            @edit="openModal('agents')"
          />
          <SchedulesSection :project="project" />
          <AssignmentsSection :project="project" />
          <SalesGallerySection :project="project" @edit="openModal('sales')" />
        </div>
      </div>
    </template>

    <div v-else class="flex-1 flex items-center justify-center">
      <p class="text-slate-500 dark:text-slate-400">
        {{ $t('REAL_ESTATE.DETAIL.NOT_FOUND') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Placeholder cover when a project has no image — the same pastel as the
   prototype, in light and dark mode alike. */
.re-cover-gradient {
  background: linear-gradient(
    135deg,
    hsl(var(--re-hue) 62% 90%),
    hsl(calc(var(--re-hue) + 45) 60% 78%)
  );
}
</style>
